import React, { useEffect, useRef } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { FaGem, FaAward, FaHandshake, FaStar, FaArrowRight } from 'react-icons/fa';
import { GiDiamondRing, GiGoldBar } from 'react-icons/gi';
import { useTheme } from '../context/ThemeContext';

const About = () => {
  const { isDarkMode } = useTheme();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroY = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const timelineData = [
    {
      year: '1995',
      title: 'The Foundation',
      desc: 'Bharti Jewellers founded with a commitment to purity and traditional craftsmanship in the heart of the city.',
      icon: <GiGoldBar size={22} />
    },
    {
      year: '2005',
      title: 'Expanding Horizons',
      desc: 'Introduced modern gold and diamond design collections, blending heritage with contemporary elegance.',
      icon: <GiDiamondRing size={22} />
    },
    {
      year: '2015',
      title: 'A Trusted Name',
      desc: 'Became a trusted jewellery destination known for quality, transparency and unmatched customer satisfaction.',
      icon: <FaAward size={20} />
    },
    {
      year: 'Today',
      title: 'Legacy Continues',
      desc: 'Continuing the legacy with innovative designs, trusted purity and generations of happy families.',
      icon: <FaStar size={20} />
    }
  ];

  const valuesData = [
    {
      icon: <FaGem size={28} />,
      title: 'Pure Quality',
      desc: 'Every piece is crafted with certified pure metals and ethically sourced gemstones.'
    },
    {
      icon: <FaHandshake size={28} />,
      title: 'Trusted Since 1995',
      desc: 'Three decades of honest dealings and transparent pricing that customers rely on.'
    },
    {
      icon: <FaAward size={28} />,
      title: 'Master Craftsmanship',
      desc: 'Our artisans bring generations of skill to every ring, necklace and bangle we create.'
    },
    {
      icon: <GiDiamondRing size={28} />,
      title: 'Timeless Designs',
      desc: 'From traditional to contemporary — jewellery designed to be treasured forever.'
    }
  ];

  const statsData = [
    { number: '30+', label: 'Years of Legacy' },
    { number: '50K+', label: 'Happy Families' },
    { number: '10K+', label: 'Unique Designs' },
    { number: '100%', label: 'Certified Purity' }
  ];

  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: (i = 0) => ({
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }
    })
  };

  const fadeIn = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.8 } }
  };

  const styles = {
    page: {
      background: isDarkMode ? 'var(--bg-primary)' : '#faf9f7',
      color: 'var(--text-primary)',
      overflowX: 'hidden'
    },
    heroSection: {
      position: 'relative',
      height: '520px',
      overflow: 'hidden',
      borderRadius: '0 0 40px 40px'
    },
    heroOverlay: {
      position: 'absolute',
      inset: 0,
      background: isDarkMode
        ? 'linear-gradient(135deg, rgba(10,8,5,0.82) 0%, rgba(26,20,8,0.75) 100%)'
        : 'linear-gradient(135deg, rgba(20,15,5,0.72) 0%, rgba(50,38,10,0.65) 100%)',
      zIndex: 1
    },
    heroContent: {
      position: 'absolute',
      inset: 0,
      zIndex: 2,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      padding: '0 20px'
    },
    heroBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      background: 'rgba(212, 175, 55, 0.18)',
      border: '1px solid rgba(212, 175, 55, 0.4)',
      borderRadius: '50px',
      padding: '6px 18px',
      fontSize: '13px',
      fontWeight: '600',
      letterSpacing: '2px',
      textTransform: 'uppercase',
      color: '#d4af37',
      marginBottom: '20px'
    },
    heroTitle: {
      fontFamily: 'var(--font-secondary)',
      fontSize: 'clamp(2.4rem, 6vw, 4rem)',
      fontWeight: '800',
      color: '#ffffff',
      lineHeight: 1.15,
      marginBottom: '16px',
      textShadow: '0 2px 20px rgba(0,0,0,0.3)'
    },
    heroSubtitle: {
      fontSize: '17px',
      color: 'rgba(255,255,255,0.82)',
      maxWidth: '560px',
      lineHeight: '1.7'
    },
    goldDivider: {
      width: '70px',
      height: '3px',
      background: 'var(--gradient-gold, linear-gradient(90deg, #b8860b, #d4af37, #f5d782))',
      borderRadius: '3px',
      margin: '0 auto'
    },
    sectionLabel: {
      fontSize: '12px',
      fontWeight: '700',
      letterSpacing: '3px',
      textTransform: 'uppercase',
      color: 'var(--primary-gold, #d4af37)',
      marginBottom: '10px'
    },
    sectionTitle: {
      fontFamily: 'var(--font-secondary)',
      fontSize: 'clamp(1.8rem, 4vw, 2.8rem)',
      fontWeight: '800',
      color: 'var(--text-primary)',
      lineHeight: 1.2,
      marginBottom: '16px'
    },
    storyCard: {
      background: isDarkMode ? 'var(--bg-secondary)' : '#ffffff',
      borderRadius: '20px',
      padding: '40px',
      border: isDarkMode ? '1px solid rgba(212,175,55,0.12)' : '1px solid rgba(0,0,0,0.06)',
      boxShadow: isDarkMode ? 'none' : '0 8px 40px rgba(0,0,0,0.06)'
    },
    statCard: {
      background: isDarkMode
        ? 'linear-gradient(135deg, rgba(212,175,55,0.08), rgba(212,175,55,0.03))'
        : 'linear-gradient(135deg, #fffbf0, #fff8e1)',
      borderRadius: '16px',
      border: `1px solid ${isDarkMode ? 'rgba(212,175,55,0.2)' : 'rgba(212,175,55,0.3)'}`,
      padding: '28px 20px',
      textAlign: 'center'
    },
    statNumber: {
      fontFamily: 'var(--font-secondary)',
      fontSize: '2.5rem',
      fontWeight: '900',
      color: 'var(--primary-gold, #d4af37)',
      lineHeight: 1,
      marginBottom: '6px'
    },
    statLabel: {
      fontSize: '13px',
      fontWeight: '500',
      color: 'var(--text-secondary)',
      letterSpacing: '0.5px'
    },
    timelineWrapper: {
      position: 'relative',
      padding: '10px 0'
    },
    timelineLine: {
      position: 'absolute',
      left: '50%',
      top: 0,
      bottom: 0,
      width: '2px',
      background: isDarkMode
        ? 'linear-gradient(to bottom, transparent, rgba(212,175,55,0.4), transparent)'
        : 'linear-gradient(to bottom, transparent, rgba(212,175,55,0.5), transparent)',
      transform: 'translateX(-50%)'
    },
    timelineDot: {
      width: '48px',
      height: '48px',
      borderRadius: '50%',
      background: 'var(--gradient-gold, linear-gradient(135deg, #b8860b, #d4af37))',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#1a1200',
      flexShrink: 0,
      boxShadow: '0 4px 16px rgba(212,175,55,0.35)'
    },
    timelineCard: {
      background: isDarkMode ? 'var(--bg-secondary)' : '#ffffff',
      borderRadius: '16px',
      padding: '24px 28px',
      border: isDarkMode ? '1px solid rgba(212,175,55,0.1)' : '1px solid rgba(0,0,0,0.06)',
      boxShadow: isDarkMode ? 'none' : '0 4px 24px rgba(0,0,0,0.06)',
      flex: 1
    },
    valueCard: {
      background: isDarkMode ? 'var(--bg-secondary)' : '#ffffff',
      borderRadius: '20px',
      padding: '32px 28px',
      border: isDarkMode ? '1px solid rgba(212,175,55,0.12)' : '1px solid rgba(0,0,0,0.06)',
      boxShadow: isDarkMode ? 'none' : '0 4px 24px rgba(0,0,0,0.05)',
      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
      cursor: 'default'
    },
    iconWrapper: {
      width: '62px',
      height: '62px',
      borderRadius: '16px',
      background: isDarkMode
        ? 'rgba(212,175,55,0.12)'
        : 'linear-gradient(135deg, #fffbf0, #fff3cd)',
      border: `1px solid ${isDarkMode ? 'rgba(212,175,55,0.25)' : 'rgba(212,175,55,0.35)'}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'var(--primary-gold, #d4af37)',
      marginBottom: '20px'
    },
    visionSection: {
      background: isDarkMode
        ? 'linear-gradient(135deg, rgba(212,175,55,0.06), rgba(212,175,55,0.02))'
        : 'linear-gradient(135deg, #fffbf0 0%, #fff8e1 100%)',
      border: `1px solid ${isDarkMode ? 'rgba(212,175,55,0.18)' : 'rgba(212,175,55,0.25)'}`,
      borderRadius: '24px',
      padding: '60px 40px',
      textAlign: 'center',
      position: 'relative',
      overflow: 'hidden'
    }
  };

  return (
    <div style={styles.page}>

      {/* ── HERO ── */}
      <motion.section ref={heroRef} style={styles.heroSection}>
        <motion.img
          src="https://images.unsplash.com/photo-1588444837495-c6cfeb53f32d?w=1400&h=600&fit=crop"
          alt="Bharti Jewellers Store"
          style={{
            position: 'absolute', inset: 0,
            width: '100%', height: '100%',
            objectFit: 'cover'
          }}
          style2={{ y: heroY }}
          initial={{ scale: 1.08 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.4, ease: 'easeOut' }}
        />
        <div style={styles.heroOverlay} />
        <motion.div style={styles.heroContent}>
          <motion.div
            style={styles.heroBadge}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}>
            <FaGem size={12} />
            Since 1995
          </motion.div>
          <motion.h1
            style={styles.heroTitle}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.35 }}>
            Our Legacy of<br />
            <span style={{ color: '#d4af37' }}>Trust & Craft</span>
          </motion.h1>
          <motion.p
            style={styles.heroSubtitle}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}>
            The journey of Bharti Jewellers is built on trust, tradition,
            craftsmanship and generations of excellence.
          </motion.p>
        </motion.div>
      </motion.section>

      {/* ── STATS BAR ── */}
      <section className="py-5">
        <div className="container">
          <div className="row g-3">
            {statsData.map((stat, i) => (
              <div className="col-6 col-md-3" key={i}>
                <motion.div
                  style={styles.statCard}
                  variants={fadeUp}
                  custom={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}>
                  <div style={styles.statNumber}>{stat.number}</div>
                  <div style={styles.statLabel}>{stat.label}</div>
                </motion.div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── OUR STORY ── */}
      <section className="py-5">
        <div className="container">
          <div className="row align-items-center g-5">
            <div className="col-lg-6">
              <motion.div
                initial={{ opacity: 0, x: -40, scale: 0.97 }}
                whileInView={{ opacity: 1, x: 0, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                style={{ position: 'relative' }}>
                <img
                  src="https://images.unsplash.com/photo-1602751584552-8ba73aad10e1?w=700&h=500&fit=crop"
                  alt="Gold Jewellery Crafting"
                  style={{
                    width: '100%',
                    borderRadius: '20px',
                    objectFit: 'cover',
                    display: 'block'
                  }}
                />
                {/* Floating badge */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.7 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.5 }}
                  style={{
                    position: 'absolute',
                    bottom: '-20px',
                    right: '-16px',
                    background: 'var(--gradient-gold, linear-gradient(135deg, #b8860b, #d4af37))',
                    borderRadius: '16px',
                    padding: '16px 22px',
                    color: '#1a1200',
                    fontWeight: '800',
                    fontSize: '13px',
                    boxShadow: '0 8px 28px rgba(212,175,55,0.45)',
                    textAlign: 'center',
                    lineHeight: 1.3
                  }}>
                  <div style={{ fontSize: '1.8rem', fontWeight: '900', lineHeight: 1 }}>30</div>
                  Years of<br />Excellence
                </motion.div>
              </motion.div>
            </div>

            <div className="col-lg-6">
              <motion.div
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}>
                <p style={styles.sectionLabel}>Our Story</p>
                <h2 style={styles.sectionTitle}>
                  Where Tradition<br />Meets Brilliance
                </h2>
                <div style={{ ...styles.goldDivider, margin: '0 0 28px' }} />
                <div style={styles.storyCard}>
                  {[
                    'Bharti Jewellers was established with a simple vision — to provide customers with pure, beautifully crafted jewellery that represents tradition and trust.',
                    'What started as a small jewellery store has grown into a well-recognized name known for quality, authenticity and exceptional customer service.',
                    'Our passion for jewellery design and commitment to craftsmanship has allowed us to serve generations of families who trust us for their most special moments.'
                  ].map((para, i) => (
                    <motion.p
                      key={i}
                      variants={fadeUp}
                      custom={i + 1}
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true }}
                      style={{
                        color: 'var(--text-secondary)',
                        lineHeight: '1.8',
                        marginBottom: i === 2 ? 0 : '16px',
                        fontSize: '15.5px'
                      }}>
                      {para}
                    </motion.p>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ── TIMELINE ── */}
      <section className="py-5" style={{
        background: isDarkMode ? 'var(--bg-secondary)' : '#f5f3ef'
      }}>
        <div className="container">
          <motion.div
            className="text-center mb-5"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}>
            <p style={styles.sectionLabel}>Milestones</p>
            <h2 style={styles.sectionTitle}>Our Journey</h2>
            <div style={styles.goldDivider} />
          </motion.div>

          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <div style={styles.timelineWrapper}>
              {/* Center line — desktop only */}
              <div className="d-none d-md-block" style={styles.timelineLine} />

              {timelineData.map((item, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  custom={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '20px',
                    marginBottom: i < timelineData.length - 1 ? '32px' : 0,
                    flexDirection: i % 2 === 0 ? 'row' : 'row-reverse'
                  }}>
                  {/* Card */}
                  <div style={{ ...styles.timelineCard, textAlign: i % 2 === 0 ? 'right' : 'left' }}>
                    <div style={{
                      fontSize: '11px',
                      fontWeight: '700',
                      letterSpacing: '2px',
                      color: 'var(--primary-gold, #d4af37)',
                      textTransform: 'uppercase',
                      marginBottom: '6px'
                    }}>
                      {item.year}
                    </div>
                    <h5 style={{
                      fontFamily: 'var(--font-secondary)',
                      fontWeight: '700',
                      fontSize: '17px',
                      color: 'var(--text-primary)',
                      marginBottom: '8px'
                    }}>
                      {item.title}
                    </h5>
                    <p style={{
                      color: 'var(--text-secondary)',
                      fontSize: '14px',
                      lineHeight: '1.6',
                      margin: 0
                    }}>
                      {item.desc}
                    </p>
                  </div>

                  {/* Dot */}
                  <div style={{ ...styles.timelineDot, flexShrink: 0 }}>
                    {item.icon}
                  </div>

                  {/* Spacer for alternating layout */}
                  <div style={{ flex: 1 }} className="d-none d-md-block" />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── OUR VALUES ── */}
      <section className="py-5">
        <div className="container">
          <motion.div
            className="text-center mb-5"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}>
            <p style={styles.sectionLabel}>What We Stand For</p>
            <h2 style={styles.sectionTitle}>Our Core Values</h2>
            <div style={styles.goldDivider} />
          </motion.div>

          <div className="row g-4">
            {valuesData.map((val, i) => (
              <div className="col-sm-6 col-lg-3" key={i}>
                <motion.div
                  style={styles.valueCard}
                  variants={fadeUp}
                  custom={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  whileHover={{ y: -6, boxShadow: isDarkMode ? '0 8px 32px rgba(212,175,55,0.12)' : '0 12px 40px rgba(0,0,0,0.1)' }}>
                  <div style={styles.iconWrapper}>
                    {val.icon}
                  </div>
                  <h5 style={{
                    fontFamily: 'var(--font-secondary)',
                    fontWeight: '700',
                    fontSize: '17px',
                    color: 'var(--text-primary)',
                    marginBottom: '10px'
                  }}>
                    {val.title}
                  </h5>
                  <p style={{
                    color: 'var(--text-secondary)',
                    fontSize: '14px',
                    lineHeight: '1.65',
                    margin: 0
                  }}>
                    {val.desc}
                  </p>
                </motion.div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── VISION ── */}
      <section className="py-5">
        <div className="container">
          <motion.div
            style={styles.visionSection}
            variants={fadeIn}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}>

            {/* Decorative circles */}
            <div style={{
              position: 'absolute', top: '-60px', right: '-60px',
              width: '220px', height: '220px', borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(212,175,55,0.12), transparent 70%)',
              pointerEvents: 'none'
            }} />
            <div style={{
              position: 'absolute', bottom: '-40px', left: '-40px',
              width: '160px', height: '160px', borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(212,175,55,0.1), transparent 70%)',
              pointerEvents: 'none'
            }} />

            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              style={{ position: 'relative', zIndex: 1 }}>
              <div style={{
                width: '56px', height: '56px', borderRadius: '50%',
                background: 'var(--gradient-gold, linear-gradient(135deg, #b8860b, #d4af37))',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 24px',
                boxShadow: '0 6px 20px rgba(212,175,55,0.4)'
              }}>
                <FaGem size={22} color="#1a1200" />
              </div>
              <p style={styles.sectionLabel}>Our Purpose</p>
              <h2 style={{ ...styles.sectionTitle, marginBottom: '20px' }}>Our Vision</h2>
              <div style={{ ...styles.goldDivider, marginBottom: '28px' }} />
              <p style={{
                color: 'var(--text-secondary)',
                fontSize: '16.5px',
                lineHeight: '1.85',
                maxWidth: '660px',
                margin: '0 auto 32px'
              }}>
                Our vision is to become a symbol of trust, purity and excellence in the
                jewellery industry. Bharti Jewellers aims to create jewellery that not
                only enhances beauty but also becomes a treasured part of life's most
                precious moments — passed down through generations.
              </p>
              <motion.a
                href="/collections"
                className="btn btn-luxury d-inline-flex align-items-center gap-2"
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.97 }}
                style={{ fontSize: '15px', padding: '12px 32px', borderRadius: '50px' }}>
                Explore Our Collections
                <FaArrowRight size={13} />
              </motion.a>
            </motion.div>
          </motion.div>
        </div>
      </section>

    </div>
  );
};

export default About;